<script setup lang="ts">
import { computed } from "vue";

import actions from "~/actions";
import RecurrenceChip from "~/components/dumb/RecurrenceChip.vue";
import ReminderChip from "~/components/dumb/ReminderChip.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import { ORDERED_RELATIONSHIP_CONFIGURATION } from "~/constants/relationship";
import {
  AttachmentFieldIcon,
  ChildRelationshipIcon,
  CommentIcon,
  DescriptionFieldIcon,
  DocIcon,
  LinkFieldIcon,
  ParentRelationshipIcon,
  RelationshipsIcon,
} from "~/icons";
import { isDefined } from "~/shared/common";
import { CommandId, EditorMode, Placement, RelationshipKindKind, SubtaskDisplayMode, ViewKind } from "~/shared/enums";
import type { TaskAbsenteeMaybe } from "~/shared/types";
import { useAppStore, useDataStore } from "~/stores";
import { asyncState } from "~/utils/async";
import { isLexicalStateEmpty } from "~/utils/common";
import { useAsyncTask, useAsyncTasksOrdered } from "~/utils/entityIncrementalLoading";

const props = defineProps<{
  task: TaskAbsenteeMaybe;
  editorMode: EditorMode;
  hasShownParent: boolean;
  subtasksExpanded: boolean;
  hideSubtasks?: boolean;
  customStyles?: boolean;
}>();

const emit = defineEmits<{
  openTaskInDetail: [event: KeyboardEvent | MouseEvent];
  jumpToDescription: [event: KeyboardEvent | MouseEvent];
  jumpToComments: [event: KeyboardEvent | MouseEvent];
  toggleSubtasks: [event: KeyboardEvent | MouseEvent];
}>();

const appStore = useAppStore();
const dataStore = useDataStore();

const containerClasses = computed(() => (props.customStyles ? "mb-1 inline-flex align-middle" : ""));

const isTrash = computed(() => appStore.currentPage?.kind === ViewKind.TRASH);
const isListMiniOrMiniTcmMode = computed(
  () => props.editorMode === EditorMode.LIST_MINI || props.editorMode === EditorMode.LIST_MINI_TCM
);
const isListMiniMode = computed(
  () => isListMiniOrMiniTcmMode.value || props.editorMode === EditorMode.LIST_MINI_ROADMAP
);
const hasDescription = computed(() => !isLexicalStateEmpty(props.task.description));
const hasAttachments = computed(() => props.task.attachmentDuids.length > 0);
const hasLinks = computed(() => props.task.links.length);

asyncState(() => dataStore.doesTaskHaveComments(props.task.duid), {
  default: dataStore.doesTaskHaveLoadedComments(props.task.duid),
  watch: [() => props.task.duid],
});
const hasComments = computed(() => dataStore.doesTaskHaveLoadedComments(props.task.duid));

const parentTaskDuid = computed(
  () =>
    dataStore.getRelationshipsByKindKind(props.task, RelationshipKindKind.PARENT_OF, false, {
      includeTrashed: isTrash.value,
    })[0]?.targetDuid
);

const { task: parentTask } = useAsyncTask(computed(() => parentTaskDuid.value));
const parentText = computed(() => `${parentTask.value?.title}`);

const subtaskDuids = computed(() =>
  dataStore
    .getRelationshipsByKindKind(props.task, RelationshipKindKind.PARENT_OF, true, { includeTrashed: isTrash.value })
    .map((e) => e.targetDuid)
);
const { tasks: subtasks } = useAsyncTasksOrdered(
  computed(() => subtaskDuids.value),
  computed(() => ({ includeTrashed: isTrash.value, includeDraft: false }))
);
const numSubtasksCompleted = computed(
  () => subtasks.value.filter((task) => dataStore.defaultCompletedStatusDuids.has(task.statusDuid)).length
);

const otherRelationships = computed(() => {
  const parentKindDuid = dataStore.getRelationshipKindByKind(RelationshipKindKind.PARENT_OF).duid;
  return dataStore
    .getRelationships(props.task, { includeTrashed: isTrash.value })
    .filter((e) => e.kindDuid !== parentKindDuid);
});
const { tasks: otherRelatedTasks } = useAsyncTasksOrdered(
  computed(() => otherRelationships.value.map((e) => e.targetDuid)),
  computed(() => ({ includeTrashed: isTrash.value, includeDraft: false }))
);
const otherRelatedTasksSections = computed(() =>
  ORDERED_RELATIONSHIP_CONFIGURATION.map(({ kind, isForward, label }) => {
    const relationshipDuid = dataStore.getRelationshipKindByKind(kind).duid;
    const matchingRelationships = otherRelationships.value.filter(
      (e) => e.kindDuid === relationshipDuid && (isForward === undefined || e.isForward === isForward)
    );
    if (matchingRelationships.length === 0) {
      return null;
    }

    const tasks = dataStore
      .getTasksByDuidsOrderedIfLoaded(
        matchingRelationships.map((e) => e.targetDuid),
        { includeTrashed: isTrash.value, includeDraft: false }
      )
      .map((task) => `${task.title}`);

    return {
      title: label,
      items: tasks,
    };
  }).filter(isDefined)
);

const notFlat = computed(() => appStore.subtaskDisplayMode !== SubtaskDisplayMode.FLAT);
const showParent = computed(() => !notFlat.value || !props.hasShownParent);

const docRelationships = computed(() => dataStore.getDocDuidsRelatedToTask(props.task));
const { state: relatedDocs, isLoading: isLoadingRelatedDocs } = asyncState(
  () => dataStore.getDocsRelatedToTaskOrdered(props.task),
  {
    default: dataStore.getDocsRelatedToTaskOrderedIfLoaded(props.task),
    watch: [() => props.task.duid, docRelationships],
  }
);
const relatedDocsSections = computed(() => [
  {
    title: "Linked docs",
    items: isLoadingRelatedDocs
      ? docRelationships.value.map((e) => dataStore.getDocByDuidIfLoaded(e)?.title ?? "Loading...")
      : relatedDocs.value.map((e) => e.title),
  },
]);

const openTaskInDetail = (event: KeyboardEvent | MouseEvent) => emit("openTaskInDetail", event);
const jumpToDescription = (event: KeyboardEvent | MouseEvent) => emit("jumpToDescription", event);
const jumpToComments = (event: KeyboardEvent | MouseEvent) => emit("jumpToComments", event);
const toggleSubtasks = (event: KeyboardEvent | MouseEvent) => emit("toggleSubtasks", event);
</script>

<template>
  <template v-if="!isListMiniMode && !task.absentee">
    <Tooltip v-if="hasDescription" :command-id="CommandId.EDIT_DESCRIPTION" :class="containerClasses">
      <button
        type="button"
        class="flex icon-md items-center justify-center rounded-sm border border-oncolor text-gray-900/30 focus-ring-none hover:bg-opposite/10 dark:text-white/20"
        tabindex="-1"
        @click="jumpToDescription"
        @keydown.enter="jumpToDescription">
        <span class="sr-only">Open description</span>
        <DescriptionFieldIcon class="icon-xs" />
      </button>
    </Tooltip>
    <Tooltip v-if="hasComments" :command-id="CommandId.VIEW_COMMENTS" :class="containerClasses">
      <button
        type="button"
        class="flex icon-md items-center justify-center rounded-sm border border-oncolor text-gray-900/30 focus-ring-none hover:bg-opposite/10 dark:text-white/20"
        tabindex="-1"
        @click="jumpToComments"
        @keydown.enter="jumpToComments">
        <span class="sr-only">View comments</span>
        <CommentIcon class="icon-xs" />
      </button>
    </Tooltip>
    <Tooltip v-if="hasAttachments" :command-id="CommandId.VIEW_ATTACHMENTS" :class="containerClasses">
      <button
        type="button"
        class="flex icon-md items-center justify-center rounded-sm border border-oncolor text-gray-900/30 focus-ring-none hover:bg-opposite/10 dark:text-white/20"
        tabindex="-1"
        @click="openTaskInDetail"
        @keydown.enter="openTaskInDetail">
        <span class="sr-only">View attachments</span>
        <AttachmentFieldIcon class="icon-xs" />
      </button>
    </Tooltip>
    <Tooltip v-if="hasLinks" :command-id="CommandId.VIEW_LINKS" :class="containerClasses">
      <button
        type="button"
        class="flex icon-md items-center justify-center rounded-sm border border-oncolor text-gray-900/30 focus-ring-none hover:bg-opposite/10 dark:text-white/20"
        tabindex="-1"
        @click="openTaskInDetail"
        @keydown.enter="openTaskInDetail">
        <span class="sr-only">View links</span>
        <LinkFieldIcon class="icon-xs" />
      </button>
    </Tooltip>
    <RecurrenceChip
      v-if="task.recurrence && task.recursNextAt"
      :editor-mode="editorMode"
      :task="task"
      :recurrence="task.recurrence"
      :recurs-next-at="task.recursNextAt"
      :container-classes="containerClasses" />
    <ReminderChip
      v-if="task.remindAt"
      :editor-mode="editorMode"
      :task="task"
      :remind-at="task.remindAt || null"
      :container-classes="containerClasses"
      @click-finished-indicator="openTaskInDetail" />
  </template>
  <Tooltip
    v-if="!isListMiniOrMiniTcmMode && parentTask && showParent && !parentTask.drafterDuid"
    :text="parentText"
    :placement="Placement.TOP"
    :class="containerClasses">
    <Tooltip text="Open parent">
      <button
        type="button"
        class="flex h-5 max-w-32 items-center justify-center gap-1 rounded-sm border border-oncolor px-1 text-vlt focus-ring-none hover:bg-opposite/10"
        tabindex="-1"
        @click.stop="actions.visualization.navigateToTask(parentTask.duid)"
        @keydown.enter.stop="actions.visualization.navigateToTask(parentTask.duid)">
        <span class="sr-only">View parent task</span>
        <ParentRelationshipIcon class="icon-xs shrink-0" />
        <span class="truncate text-xs text-md">{{ parentText }}</span>
      </button>
    </Tooltip>
  </Tooltip>
  <Tooltip
    v-if="!hideSubtasks && subtasks.length > 0"
    :command-id="
      notFlat && !isListMiniOrMiniTcmMode
        ? subtasksExpanded
          ? CommandId.HIDE_SUBTASKS
          : CommandId.SHOW_SUBTASKS
        : undefined
    "
    :text="isListMiniOrMiniTcmMode ? 'Subtasks' : !notFlat ? 'Open task to see subtasks' : undefined"
    :class="containerClasses">
    <button
      type="button"
      class="flex h-5 items-center justify-center gap-0.5 rounded-sm border border-oncolor px-0.5 text-gray-900/30 focus-ring-none hover:bg-opposite/10 dark:text-white/20"
      tabindex="-1"
      @click="toggleSubtasks"
      @keydown.enter="toggleSubtasks">
      <span class="sr-only">Toggle subtasks</span>
      <ChildRelationshipIcon class="icon-xs" />
      <span class="text-xs">{{ `${numSubtasksCompleted}/${subtasks.length}` }}</span>
    </button>
  </Tooltip>
  <template v-if="!isListMiniMode && !task.absentee">
    <Tooltip
      v-if="otherRelatedTasks.length > 0"
      :sections="otherRelatedTasksSections"
      :placement="Placement.TOP"
      :class="containerClasses">
      <Tooltip text="Open task to see relationships">
        <button
          type="button"
          class="flex h-5 items-center justify-center gap-0.5 rounded-sm border border-oncolor px-0.5 text-gray-900/30 focus-ring-none hover:bg-opposite/10 dark:text-white/20"
          tabindex="-1"
          @click="openTaskInDetail"
          @keydown.enter="openTaskInDetail">
          <span class="sr-only">View other relationships</span>
          <RelationshipsIcon class="icon-xs" />
          <span class="text-xs">{{ otherRelatedTasks.length }}</span>
        </button>
      </Tooltip>
    </Tooltip>
    <Tooltip
      v-if="(isLoadingRelatedDocs && docRelationships.length > 0) || relatedDocs.length > 0"
      :sections="relatedDocsSections"
      :class="containerClasses">
      <button
        type="button"
        class="flex h-5 items-center justify-center gap-0.5 rounded-sm border border-oncolor px-0.5 text-gray-900/30 focus-ring-none hover:bg-opposite/10 dark:text-white/20"
        tabindex="-1"
        @click="openTaskInDetail"
        @keydown.enter="openTaskInDetail">
        <span class="sr-only">View related docs</span>
        <DocIcon class="icon-xs" />
        <span class="text-xs">
          {{ isLoadingRelatedDocs ? docRelationships.length : relatedDocs.length }}
        </span>
      </button>
    </Tooltip>
  </template>
</template>
